import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import {
  Color,
  Text,
  FontWeight,
  withCitrusTheme,
  DisplayPosition,
  Box,
  Display,
  JustifyContent,
  TextAlign,
  AlignItems,
  ZIndex,
  BorderStyle,
} from '@gousto-internal/citrus-react'

import type {
  RecipeUUID,
  IngredientCustomisationSlotValue,
} from '@library/api-menu-experience/src/interfaces/recipe'

import { CrossOut } from './CrossOut'
import { Tooltip } from './Tooltip'
import { queueTrackingEvent } from '@library/tracking-integration'

const PillText = ({ name }: { name: string }) => (
  <Text
    size={1}
    fontWeight={FontWeight.Bold}
    textAlign={TextAlign.Center}
    style={{ whiteSpace: 'nowrap', zIndex: ZIndex.Top }}
  >
    {name}
  </Text>
)

const SelectedPill = ({ children }: { children: ReactNode }) => {
  const Component = withCitrusTheme(Box, (theme) => ({
    width: '100%',
    height: '40px',
    color: theme.color(Color.ColdGrey_800),
    backgroundColor: theme.color(Color.Secondary_50),
    borderStyle: BorderStyle.Solid,
    borderColor: theme.color(Color.Secondary_400),
    borderRadius: '100px',
    borderWidth: '2px',
    display: Display.Flex,
    justifyContent: JustifyContent.Center,
    alignItems: AlignItems.Center,
    paddingRight: '20px',
    paddingLeft: '20px',
  }))

  return <Component data-testid="selected-pill">{children}</Component>
}

const SelectablePill = ({
  isAvailable,
  children,
  onClick,
}: {
  isAvailable: boolean
  children: ReactNode
  onClick: () => void
}) => {
  const Component = withCitrusTheme(Box, (theme) => ({
    cursor: 'pointer',
    width: '100%',
    height: '40px',
    color: theme.color(isAvailable ? Color.ColdGrey_800 : Color.ColdGrey_500),
    backgroundColor: theme.color(Color.NeutralGrey_50),
    borderStyle: BorderStyle.Solid,
    borderColor: theme.color(Color.NeutralGrey_200),
    display: Display.Flex,
    justifyContent: JustifyContent.Center,
    alignItems: AlignItems.Center,
    borderRadius: '100px',
    borderWidth: '1px',
    paddingRight: '20px',
    paddingLeft: '20px',
    position: DisplayPosition.Relative,
    '&:hover': {
      backgroundColor: theme.color(Color.NeutralGrey_100),
      color: theme.color(Color.ColdGrey_800),
    },
    '&:active, &:focus': {
      outline: '2px solid #656667',
      borderColor: theme.color(Color.NeutralGrey_600),
    },
  }))

  return (
    <Component data-testid="selectable-pill" tabIndex={0} role="button" onClick={onClick}>
      {children}
    </Component>
  )
}

const usePillState = (slot: IngredientCustomisationSlotValue, recipeId: RecipeUUID) => {
  const isSelected = slot.recipeId === recipeId
  const isNotOffered = !isSelected && !slot.recipeId
  const isSoldOut = !isSelected && !!slot.recipeId && !slot.isAvailable
  const isUnavailable = isSoldOut || isNotOffered
  const isAvailable = !isUnavailable

  return { isSelected, isNotOffered, isSoldOut, isUnavailable, isAvailable }
}

const useClickOutside = (callback: () => void) => {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [ref, callback])

  return ref
}

type TrackTapUnavailableCustomisation = {
  action: 'tap_unavailable_customisation'
  properties: {
    unavailability_reason: string
    recipe_uuid?: RecipeUUID
    base_recipe_uuid?: RecipeUUID
    event_screen: string
  }
}

type TrackSelectRecipeCustomisation = {
  action: 'select_recipe_customisation'
  properties: {
    recipe_uuid: RecipeUUID
    base_recipe_uuid?: RecipeUUID
    event_screen: string
  }
}

type IngredientPillProps = {
  slot: IngredientCustomisationSlotValue
  recipeId: RecipeUUID
  view: string
  onChangeSelection: (id: RecipeUUID) => void
}

export const IngredientPill = ({
  slot,
  recipeId,
  view,
  onChangeSelection,
}: IngredientPillProps) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const tooltipRef = useClickOutside(() => setShowTooltip(false))

  const { isSelected, isNotOffered, isSoldOut, isUnavailable, isAvailable } = usePillState(
    slot,
    recipeId,
  )

  const tooltipText = 'Sorry, this isn’t available with the other options you’ve selected'

  const handleClick = useCallback(() => {
    // 1. Do nothing if the item is already selected.
    if (isSelected) {
      return
    }

    // 2. If the item is not offered, show a tooltip and track the event.
    if (isNotOffered) {
      setShowTooltip(true)
      queueTrackingEvent<TrackTapUnavailableCustomisation>({
        action: 'tap_unavailable_customisation',
        properties: {
          unavailability_reason: 'unavailable_combination',
          recipe_uuid: recipeId,
          event_screen: view,
        },
      })
      return
    }

    // 3. For all other cases (Available or Sold Out), change the selection and track.
    // We allow selecting out-of-stock items.
    const newRecipeId = slot.recipeId as RecipeUUID
    onChangeSelection(newRecipeId)

    if (isSoldOut) {
      queueTrackingEvent<TrackTapUnavailableCustomisation>({
        action: 'tap_unavailable_customisation',
        properties: {
          unavailability_reason: 'sold_out',
          recipe_uuid: recipeId,
          event_screen: view,
        },
      })
    } else {
      queueTrackingEvent<TrackSelectRecipeCustomisation>({
        action: 'select_recipe_customisation',
        properties: {
          recipe_uuid: newRecipeId,
          event_screen: view,
        },
      })
    }
  }, [isSelected, isNotOffered, isSoldOut, recipeId, view, slot.recipeId, onChangeSelection])

  return (
    <Box position={DisplayPosition.Relative}>
      {isSelected ? (
        <SelectedPill>
          <PillText name={slot.name} />
        </SelectedPill>
      ) : (
        <SelectablePill isAvailable={isAvailable} onClick={handleClick}>
          <Box
            width="100%"
            display={Display.Flex}
            justifyContent={JustifyContent.Center}
            alignItems={AlignItems.Center}
            position={DisplayPosition.Absolute}
            top="0px"
            right="0px"
            bottom="0px"
            left="0px"
          >
            <Box width={['94%', '80%']} height="99%">
              {isUnavailable && <CrossOut />}
            </Box>
          </Box>
          <PillText name={slot.name} />
        </SelectablePill>
      )}
      {showTooltip && (
        <Box
          ref={tooltipRef}
          position={DisplayPosition.Absolute}
          top="100%"
          left="50%"
          zIndex={ZIndex.Top}
          style={{ transform: 'translateX(-50%)', marginTop: '15px' }}
        >
          <Tooltip text={tooltipText} onClose={() => setShowTooltip(false)} />
        </Box>
      )}
    </Box>
  )
}
